import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import { 
  Handshake, 
  MessageCircle, 
  X, 
  TrendingUp, 
  Calendar,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { MatchService, MatchConnectionResult } from '../services/matchService';
import { Button } from './design-system/Button';
import { Heading2, Body, BodySmall } from './design-system/Typography';

interface MatchConnectionModalProps {
  isOpen: boolean;
  currentUser: User;
  matchedUser: User;
  matchType: 'normal' | 'super';
  onClose: () => void;
  onConnectionSuccess: (conversationId: string) => void;
}

type ConnectionState = 'initial' | 'connecting' | 'success' | 'error';

/**
 * Enhanced Match Connection Modal
 * Handles the complete flow from match to conversation creation
 */
export const MatchConnectionModal: React.FC<MatchConnectionModalProps> = ({
  isOpen,
  currentUser,
  matchedUser,
  matchType,
  onClose,
  onConnectionSuccess
}) => {
  const navigate = useNavigate();
  const [connectionState, setConnectionState] = useState<ConnectionState>('initial');
  const [connectionResult, setConnectionResult] = useState<MatchConnectionResult | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Animation effect
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Record match analytics
      MatchService.recordMatchAnalytics(currentUser, matchedUser, matchType);
    } else {
      setConnectionState('initial');
      setConnectionResult(null);
    }
  }, [isOpen, currentUser, matchedUser, matchType]);

  /**
   * Handles the connection process
   */
  const handleStartConnection = async () => {
    setConnectionState('connecting');
    
    try {
      console.log('🚀 Iniciando proceso de conexión...');
      
      const result = await MatchService.handleMatchConnection(currentUser, matchedUser);
      
      setConnectionResult(result);
      
      if (result.success && result.conversation) {
        console.log('✅ Conexión exitosa, conversación ID:', result.conversation.id);
        setConnectionState('success');
        
        // Wait a moment to show success state, then navigate
        setTimeout(() => {
          console.log('🚀 Navegando al chat:', result.conversation!.id);
          onConnectionSuccess(result.conversation!.id);
        }, 1500);
        
      } else {
        setConnectionState('error');
        console.error('❌ Error en conexión:', result.error);
      }
      
    } catch (error) {
      console.error('❌ Error inesperado en conexión:', error);
      setConnectionState('error');
      setConnectionResult({
        success: false,
        error: 'Error inesperado al conectar'
      });
    }
  };

  /**
   * Handles scheduling a meeting instead of immediate chat
   */
  const handleScheduleMeeting = () => {
    // In a real app, this would open a calendar/scheduling interface
    console.log('📅 Programar reunión con:', matchedUser.name);
    onClose();
  };

  /**
   * Renders the appropriate content based on connection state
   */
  const renderContent = () => {
    switch (connectionState) {
      case 'connecting':
        return (
          <div className="text-center text-white">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <Loader2 className="w-20 h-20 animate-spin text-white" />
              <div className="absolute inset-0 animate-pulse">
                <Handshake className="w-20 h-20 text-white/50" />
              </div>
            </div>
            <Heading2 className="text-white mb-4">Conectando...</Heading2>
            <Body className="text-white/90">
              {connectionResult?.isNewConversation === false 
                ? 'Cargando conversación existente...'
                : 'Creando nueva conversación...'
              }
            </Body>
          </div>
        );

      case 'success':
        return (
          <div className="text-center text-white">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <CheckCircle className="w-20 h-20 text-green-400 animate-bounce" />
            </div>
            <Heading2 className="text-white mb-4">¡Conectados!</Heading2>
            <Body className="text-white/90 mb-4">
              {connectionResult?.isNewConversation 
                ? 'Nueva conversación creada exitosamente'
                : 'Conversación existente encontrada'
              }
            </Body>
            <BodySmall className="text-white/75">
              Redirigiendo al chat...
            </BodySmall>
          </div>
        );

      case 'error':
        return (
          <div className="text-center text-white">
            <div className="w-20 h-20 mx-auto mb-6">
              <AlertCircle className="w-20 h-20 text-red-400" />
            </div>
            <Heading2 className="text-white mb-4">Error de Conexión</Heading2>
            <Body className="text-white/90 mb-6">
              {connectionResult?.error || 'No se pudo establecer la conexión'}
            </Body>
            <div className="space-y-3">
              <Button
                onClick={handleStartConnection}
                variant="secondary"
                size="lg"
                fullWidth
              >
                Reintentar Conexión
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                size="md"
                fullWidth
                className="text-white border-white/30 hover:bg-white/10"
              >
                Cerrar
              </Button>
            </div>
          </div>
        );

      default: // 'initial'
        return (
          <div className="text-center text-white max-w-sm w-full">
            {/* Match animation */}
            <div className={`mb-8 transition-all duration-1000 ${isVisible ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
              <div className="relative">
                <Handshake className="w-20 h-20 mx-auto text-white animate-pulse" />
                <div className="absolute inset-0 animate-ping">
                  <TrendingUp className="w-20 h-20 mx-auto text-white/50" />
                </div>
              </div>
            </div>

            {/* Match text */}
            <h1 className="text-5xl font-bold mb-2 animate-bounce">IT'S A</h1>
            <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-300 to-white bg-clip-text text-transparent">
              {matchType === 'super' ? 'SUPER MATCH!' : 'MATCH!'}
            </h2>
            <Body className="text-lg mb-6 opacity-90">
              {currentUser.userType === 'entrepreneur' ? 'Investor' : 'Entrepreneur'} connection found
            </Body>

            {/* User photos */}
            <div className={`flex justify-center items-center space-x-4 mb-8 transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="relative">
                <img
                  src={currentUser.images[0]}
                  alt={currentUser.name}
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover object-center"
                />
              </div>
              <div className="flex flex-col items-center space-y-1">
                <Handshake className="w-8 h-8 text-white animate-pulse" />
                <BodySmall className="text-xs opacity-75">Partnership</BodySmall>
              </div>
              <div className="relative">
                <img
                  src={matchedUser.images[0]}
                  alt={matchedUser.name}
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover object-center"
                />
              </div>
            </div>

            <Body className="text-lg mb-8 opacity-90">
              You and {matchedUser.name} are interested in connecting!
            </Body>

            {/* Action buttons */}
            <div className={`space-y-4 transition-all duration-1000 delay-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <Button
                onClick={handleStartConnection}
                variant="primary"
                size="lg"
                fullWidth
                icon={MessageCircle}
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Connect & Start Conversation
              </Button>
              
              <Button
                onClick={handleScheduleMeeting}
                variant="secondary"
                size="lg"
                fullWidth
                icon={Calendar}
                className="bg-white/20 border-2 border-white text-white hover:bg-white/30"
              >
                Schedule Meeting
              </Button>
              
              <Button
                onClick={onClose}
                variant="ghost"
                size="md"
                fullWidth
                className="bg-transparent border border-white/50 text-white hover:bg-white/10"
              >
                Continue Exploring
              </Button>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-green-500 z-50 flex items-center justify-center p-4">
      {/* Close button - only show in initial state */}
      {connectionState === 'initial' && (
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          aria-label="Cerrar modal"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      )}

      {renderContent()}
    </div>
  );
};